/**
 * Vorlage „Marinieren“.
 *
 * Fleisch liegt in einer Form. Die Marinade wird darübergegossen, Kräuter
 * fallen darauf, dann kommt ein Deckel darüber und die Form zieht im
 * Kühlschrank durch.
 */
import { ease, lerp, random, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path, roundedRectPath } from '../paths.ts';
import { circle, compact, faded, group, line, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { meatPiece } from '../parts/food.ts';
import { pourer, stream } from '../parts/kitchen.ts';
import { arrow, BADGE_RIGHT, counter, durationLabel, GROUND_Y, shadow, timerBadge } from '../parts/stage.ts';
import { minutesFromText } from '../inference.ts';
import { defineTemplate, numberValue } from '../template.ts';

/** Auflaufform. */
const DISH = { x: 36, width: 148, top: 98, height: 36 } as const;
const LIP = { x: 168, y: 30 } as const;

/** Zeitplan. */
const TIME = {
  cupIn: [0.02, 0.08],
  tilt: [0.08, 0.16],
  pour: [0.14, 0.4],
  untilt: [0.38, 0.46],
  cupOut: [0.44, 0.5],
  herbs: [0.46, 0.58],
  cover: [0.6, 0.72],
  chill: [0.72, 0.9],
  reset: [0.9, 1],
} as const;

/** Form: Innenfläche oben und Vorderwand. */
function dishParts(): { back: SceneNode[]; front: SceneNode[]; inside: string } {
  const inside = roundedRectPath(DISH.x + 6, DISH.top - 12, DISH.width - 12, 20, 6);
  const front = path()
    .move(DISH.x, DISH.top)
    .line(DISH.x + DISH.width, DISH.top)
    .line(DISH.x + DISH.width - 8, GROUND_Y)
    .line(DISH.x + 8, GROUND_Y)
    .close()
    .toString();
  return {
    back: [
      shape(roundedRectPath(DISH.x, DISH.top - 16, DISH.width, 26, 8), styles.solid('paper')),
      shape(inside, styles.solid('shade')),
    ],
    front: [
      shape(front, styles.solid('paper')),
      line(DISH.x + 12, DISH.top + 8, DISH.x + DISH.width - 12, DISH.top + 8, styles.fine('shade')),
    ],
    inside,
  };
}

/** Schneeflocke als Zeichen für den Kühlschrank. */
function snowflake(cx: number, cy: number): SceneNode {
  const arms: SceneNode[] = [0, 60, 120].map((angle) =>
    group(
      [
        line(cx - 10, cy, cx + 10, cy, styles.detail()),
        line(cx + 6, cy, cx + 9, cy - 3, styles.fine('ink')),
        line(cx + 6, cy, cx + 9, cy + 3, styles.fine('ink')),
        line(cx - 6, cy, cx - 9, cy - 3, styles.fine('ink')),
        line(cx - 6, cy, cx - 9, cy + 3, styles.fine('ink')),
      ],
      { transform: { rotate: angle, ox: cx, oy: cy } },
    ),
  );
  return group(arms);
}

/** Vorlage „Marinieren“. */
export const prepMarinateTemplate = defineTemplate({
  id: 'prep.marinate',
  group: 'prep',
  title: 'Marinieren',
  description:
    'Das Fleisch wird mit der Marinade übergossen, mit Kräutern bestreut, abgedeckt und im Kühlschrank ziehen gelassen.',
  durationSeconds: 7,
  keywords: ['marinieren', 'marinade', 'mariniert', 'einlegen', 'ziehen lassen', 'über nacht'],
  stillTime: 0.55,
  parameters: [
    {
      kind: 'number',
      name: 'minutes',
      label: 'Dauer',
      min: 15,
      max: 1440,
      step: 15,
      unit: 'Min',
      defaultValue: 120,
    },
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    return { minutes: lower.includes('über nacht') ? 720 : minutesFromText(text) };
  },
  scene(values, t) {
    const minutes = numberValue(values, 'minutes', 120);
    const dish = dishParts();
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const pour = ease.inOutSine(segment(t, ...TIME.pour)) * (1 - reset);
    const flow =
      segment(t, TIME.pour[0], TIME.pour[0] + 0.03) * (1 - segment(t, TIME.pour[1] - 0.03, TIME.pour[1]));
    const tilt =
      -72 * (ease.inOutCubic(segment(t, ...TIME.tilt)) - ease.inOutCubic(segment(t, ...TIME.untilt)));
    const cup = ease.outCubic(segment(t, ...TIME.cupIn)) * (1 - segment(t, ...TIME.cupOut));
    const herbs = segment(t, ...TIME.herbs);
    const cover = ease.inOutCubic(segment(t, ...TIME.cover)) * (1 - reset);
    const chill = segment(t, TIME.chill[0], TIME.chill[0] + 0.03) * (1 - reset);

    const pieces: SceneNode[] = [-48, -16, 16, 48].map((dx, index) =>
      meatPiece(DISH.x + DISH.width / 2 + dx, DISH.top - 6 + (index % 2) * 2, 28, 14, 0, 0),
    );
    // Marinade: ein Spiegel um die Stücke, später glänzende Tropfen darauf.
    const pool =
      pour > 0.01
        ? group([rect(DISH.x, DISH.top - 6 - pour * 6, DISH.width, 30, 0, styles.flat('deep'))], {
            clip: dish.inside,
          })
        : null;
    const gloss: SceneNode[] =
      pour > 0.5
        ? [-48, -16, 16, 48].map((dx) =>
            shape(
              path()
                .move(DISH.x + DISH.width / 2 + dx - 8, DISH.top - 11)
                .quad(
                  DISH.x + DISH.width / 2 + dx,
                  DISH.top - 15,
                  DISH.x + DISH.width / 2 + dx + 8,
                  DISH.top - 11,
                )
                .toString(),
              {
                stroke: 'deep',
                strokeWidth: STROKE.outline,
              },
            ),
          )
        : [];
    // Kräuter: fallen während des Streuens und bleiben dann auf dem Fleisch liegen.
    const rng = random(21);
    const specks: SceneNode[] = Array.from({ length: 14 }, (_, index) => {
      const x = DISH.x + 24 + rng() * (DISH.width - 48);
      const y = DISH.top - 12 + rng() * 5;
      const at = index / 14;
      const drop = ease.inQuad(segment(herbs, at * 0.6, at * 0.6 + 0.4));
      return drop > 0 ? circle(x, lerp(40, y, drop), 1.8, styles.flat('ink')) : group([]);
    });
    const leaves = herbs > 0 ? faded(1 - reset, specks) : null;
    // Deckel senkt sich von oben auf die Form.
    const lidY = lerp(-36, 0, cover);
    const lidNode =
      cover > 0.01
        ? faded(Math.min(1, cover * 2), [
            group(
              [
                shape(
                  roundedRectPath(DISH.x - 4, DISH.top - 20, DISH.width + 8, 12, 5),
                  styles.solid('tint'),
                ),
                line(
                  DISH.x + 20,
                  DISH.top - 14,
                  DISH.x + DISH.width - 20,
                  DISH.top - 14,
                  styles.fine('paper'),
                ),
              ],
              {
                transform: { y: lidY },
              },
            ),
          ])
        : null;
    return compact([
      ...counter(),
      shadow(DISH.x + DISH.width / 2, DISH.width * 0.48),
      ...dish.back,
      pool,
      ...pieces,
      ...gloss,
      leaves,
      ...dish.front,
      lidNode,
      flow > 0.02
        ? stream(
            [LIP, { x: LIP.x - 10, y: LIP.y + 30 }, { x: DISH.x + DISH.width / 2 + 20, y: DISH.top - 10 }],
            6 * flow,
            t,
            'deep',
          )
        : null,
      faded(cup, [pourer(LIP, 26, 32, tilt, lerp(0.8, 0.1, segment(t, ...TIME.pour)), 'deep')]),
      faded(chill, [snowflake(48, 30), timerBadge(BADGE_RIGHT, 30, durationLabel(minutes), t)]),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: 196, y: 24 },
        { x: 176, y: 60 },
        { x: 140, y: 82 },
      ]),
    ];
  },
});
